// TotalAdminTransactionsReport.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import CollectionTotals from '../../components/CollectionTotals';
import { formatDateTimeIST } from '../../utils/dateFormat';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const TotalAdminTransactionsReport = () => {
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { can } = usePermissions();
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]
  const [totalsByCategory, setTotalsByCategory] = useState(null); // whole-range collection tiles

  const validateDates = useCallback(() => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    return true;
  }, [startDate, endDate]);

  const fetchTransactionData = useCallback(async () => {
    if (!validateDates()) {
      return;
    }
    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      const response = await axios.post(
        `${API_BASE_URL}/adminReport/totalTransactions`,
        {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE]
        },
        {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        // [CASH-MODE] client-side guard in case the API ignores the filter
        const txs = filterRowsByMode(data.transactions || [], paymentMode).map((item, index) => ({
          id: item._id || index,
          serialNumber: index + 1,
          dateTime: formatDateTimeIST(item.dateTime), // API sends the real payment instant; shown in IST
          stateAdminId: item.stateAdminId || 'N/A',
          msbId: item.msbId || 'N/A',
          merchantId: item.merchantId || item.businessName || 'N/A',
          customerId: item.customerId || '-',
          customerName: item.customerName || 'N/A',
          customerMobile: item.customerMobile || '-',
          customerVpaId: item.customerVpaId || '-',
          customerType: item.customerType || 'Customer',
          type: item.type || 'Credit',
          totalAmount: item.totalAmount || '0.00',
          commissionPercentage: item.commissionPercentage || '0.00',
          totalIncome: item.totalIncome || '0.00',
          gstAmount: item.gstAmount || '0.00',
          balanceIncome: item.balanceIncome || '0.00',
          customerAmount: item.customerAmount || '0.00',
          customerWelfareAmount: item.customerWelfareAmount || '0.00',
          headOfficeAmount: item.headOfficeAmount || '0.00',
          stateAdminAmount: item.stateAdminAmount || '0.00',
          msbAmount: item.msbAmount || '0.00',
          paymentMode: item.paymentMode, // [CASH-MODE]
        }));

        setTransactions(txs);
        setGrandTotal(data.grandTotal || 0);
        setTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
        setTotalsByCategory(data.totalsByCategory || null);
      } else {
        setTransactions([]);
        setGrandTotal(0);
        setTotalsByMode(null); // [CASH-MODE]
        setTotalsByCategory(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setTransactions([]);
      setGrandTotal(0);
      setTotalsByMode(null); // [CASH-MODE]
      setTotalsByCategory(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, validateDates, paymentMode]); // [CASH-MODE] paymentMode

  useEffect(() => {
    fetchTransactionData();
  }, [fetchTransactionData]);

  // Export CSV (permission gated)
  const handleExportCSV = () => {
    if (!can('reports.adminTransactions.export')) return;
    if (transactions.length === 0) {
      setDialogMessage('No data to export.');
      setOpenDialog(true);
      return;
    }

    const headers = [
      'SL No',
      'Date & Time',
      'State Head ID',
      'Q-Pay Center ID',
      'Merchant ID',
      'Customer ID',
      'Customer Name',
      'Customer Mobile',
      'Customer VPA ID',
      'Customer Type',
      'Type (Credit/Debit)',
      'Mode', // [CASH-MODE]
      'Original Transaction Amount',
      'Commission %',
      'Commission amount',
      'Commission GST',
      'Balance',
      'Customer %',
      'Welfare %',
      'Company %',
      'State Head %',
      'Q-Pay Center %'
    ];

    const rows = transactions.map((t) => [
      t.serialNumber,
      `"${t.dateTime}"`,
      `"${t.stateAdminId}"`,
      `"${t.msbId}"`,
      `"${t.merchantId}"`,
      `"${t.customerId}"`,
      `"${t.customerName}"`,
      `"${t.customerMobile}"`,
      `"${t.customerVpaId}"`,
      `"${t.customerType}"`,
      `"${t.type}"`,
      paymentModeLabel(t), // [CASH-MODE]
      t.totalAmount,
      t.commissionPercentage,
      t.totalIncome,
      t.gstAmount,
      t.balanceIncome,
      t.customerAmount,
      t.customerWelfareAmount,
      t.headOfficeAmount,
      t.stateAdminAmount,
      t.msbAmount
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `SUPER_ADMIN_Transaction_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: 'serialNumber', headerName: 'SL No', width: 70 },
    { field: 'dateTime', headerName: 'Date & Time', width: 200 },
    { field: 'stateAdminId', headerName: 'State Head ID', width: 130 },
    { field: 'msbId', headerName: 'Q-Pay Center ID', width: 130 },
    { field: 'merchantId', headerName: 'Merchant ID', width: 140 },
    { field: 'customerId', headerName: 'Customer ID', width: 130 },
    { field: 'customerName', headerName: 'Customer Name', width: 150 },
    { field: 'customerMobile', headerName: 'Customer Mobile', width: 140 },
    { field: 'customerVpaId', headerName: 'Customer VPA ID', width: 180 },
    {
      field: 'customerType',
      headerName: 'Customer Type',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Customer' ? 'success' : 'warning'}
          variant="outlined"
          sx={{ fontWeight: 'bold' }}
        />
      ),
    },
    { field: 'type', headerName: 'Type', width: 90 },
    // [CASH-MODE]
    { field: 'paymentMode', headerName: 'Mode', width: 90, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    { field: 'totalAmount', headerName: 'Original Amount (₹)', width: 160 },
    { field: 'commissionPercentage', headerName: 'Commission %', width: 120 },
    { field: 'totalIncome', headerName: 'Commission Amount (₹)', width: 170 },
    { field: 'gstAmount', headerName: 'Commission GST (₹)', width: 140 },
    { field: 'balanceIncome', headerName: 'Balance (₹)', width: 120 },
    { field: 'customerAmount', headerName: 'Customer %', width: 110 },
    { field: 'customerWelfareAmount', headerName: 'Welfare %', width: 110 },
    { field: 'headOfficeAmount', headerName: 'Company %', width: 110 },
    { field: 'stateAdminAmount', headerName: 'State Head %', width: 120 },
    { field: 'msbAmount', headerName: 'Q-Pay Center %', width: 130 },
  ];

  // [CASH-MODE] prefer API totals, fall back to loaded rows
  const modeTotals = totalsByMode || totalsByModeFromRows(transactions, (r) => r.totalAmount);
  const modeAmountKey = modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount';

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <br /><br />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              SUPER ADMIN Transaction Report
            </Typography>

            {/* CSV Export Button (permission gated) */}
            {can('reports.adminTransactions.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 'bold', borderRadius: 2 }}
              >
                Export CSV
              </Button>
            )}
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            {/* [CASH-MODE] */}
            <Grid item xs={12} sm={4}>
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchTransactionData}
                disabled={loading}
                sx={{ py: 1.5, px: 4, fontWeight: 'bold' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
              </Button>
            </Grid>
          </Grid>

          {/* [CASH-MODE] grand total (fetched earlier but not shown) + UPI/Cash split */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Grand Total: ₹{(Number(grandTotal) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          <CollectionTotals totals={totalsByCategory} sx={{ mb: 2 }} />

          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={transactions}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              loading={loading}
              disableSelectionOnClick
              rowHeight={60}
            />
          </Box>
        </Paper>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Notification</DialogTitle>
          <DialogContent>
            <Typography>{dialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalAdminTransactionsReport;
